import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DataTable, columnTypes as colType, ratio, rvERA, dateTranslate } from 'yankees-react-utils';
import moment from 'moment';
import { getCellShadingClass } from '../../../../utils/formatters';
import { getLevelAvgAndStDev } from '../../../../utils/helpers';
import usePlayerSelector from '../../../../utils/usePlayerSelector';
import PitchLog from './PitchLog';
import GameLog from './GameLog';
import VideoPlayerButton from '../../../../components/VideoPlayerButton';
import VideoPlaylistButton from '../../../../components/VideoPlaylistButton';
import StatTooltip from '../../../../components/StatTooltip';
import DrilldownParent from './DrilldownParent';

const SuperHeaderRow = styled.tr`
    background-color: #C6D3DC;
`;
const SuperHeaderCell = styled.th`
    && {
        border-left: 1px solid #FFF;
        border-right: 1px solid #FFF;
        background-color: #244C74 !important;
        color: #FFF;
        text-align: center;
    }
`;

const getStlyingClass = (...args) => `text-center ${getCellShadingClass(...args)}`;

const PitchTypeStats = ({ playerid, gamestats, seasonstats, lgastats, islhp, pitchtypestdev, isInBats, startDate, endDate, pitchTypeSplits, isLive, livePlaylistFn, useSubType, showGameFxEra }) => {
    const { t: translate } = useTranslation();
    const { IsMlbOrAAA } = usePlayerSelector();

    const columns = [
        // Usage
        { id: 'pitchlabel',
            title: translate('sections.reports.pitcher.abbr-num-pitches'),
            statistic: 'Pitches',
            transform: (row) => row.pitchlabel || row.pitches,
            cellClass: 'text-center',
            headerClass: 'text-center' },
        { id: 'pit%',
            title: translate('sections.reports.pitcher.usage-pct'),
            statistic: 'PitchTypePercent',
            transform: (row) => ratio(row.pitches, row.totalpitches),
            type: colType.PERCENT,
            cellClass: 'text-center',
            headerClass: 'text-center' },
        // Pitch Profile
        { id: 'initspeed54', title: translate('sections.reports.pitcher.abbr-velo'), statistic: 'AvgVelo', precision: 1 },
        { id: 'breakx54', title: translate('sections.reports.pitcher.horiz-break'), statistic: 'BreakX', precision: 1 },
        { id: 'breakz54', title: translate('sections.reports.pitcher.vert-break'), statistic: 'BreakZ', precision: 1 },
        { id: 'spinrate', title: translate('sections.reports.pitcher.spin-rate'), statistic: 'SpinRate' },
        {
            id: 'spindirectionclock',
            title: translate('sections.reports.pitcher.spin-dir'),
            statistic: 'SpinDir',
            type: colType.NODE,
            cellClass: 'text-center',
            headerClass: 'text-center',
        },
        !isLive && { id: 'gyrodegree', title: translate('sections.reports.pitcher.abbr-gyro-degree'), statistic: 'GyroDeg', precision: 1 },
        // Release Metrics
        { id: 'initposx', title: translate('sections.reports.pitcher.rel-side'), statistic: 'ReleaseX', precision: 1 },
        { id: 'initposz', title: translate('sections.reports.pitcher.rel-height'), statistic: 'ReleaseZ', precision: 1 },
        { id: 'extension', title: translate('sections.reports.pitcher.abbr-extension'), statistic: 'Extension', precision: 2 },
        !isLive && { id: 'armangle', title: translate('sections.reports.pitcher.abbr-arm-angle'), statistic: 'ArmAngle', precision: 0 },
        // Controlling the Zone
        {
            id: 'k%',
            title: translate('sections.reports.pitcher.strike-pct'),
            statistic: 'Stk',
            transform: (row) => ratio(row.stk, row.pitches),
            type: colType.PERCENT,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass(row.stk, row.pitches, row.strike_avg, row.strike_stdev),
        },
        !isLive && {
            id: 'pfxkzon%',
            title: translate('sections.reports.pitcher.zone-pct'),
            statistic: 'ExpStkPercentAll',
            transform: (row) => ratio(row.expstk_all, row.exppit_all),
            type: colType.PERCENT,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass(row.expstk_all, row.exppit_all, row.inz_avg, row.inz_stdev),
        },
        !isLive && {
            id: 'closepitch%',
            title: translate('sections.reports.pitcher.close-pitch-pct'),
            statistic: 'ClosePitch',
            transform: (row) => ratio(row.closepitch, row.pitches),
            type: colType.PERCENT,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass(row.closepitch, row.pitches, row.closepitch_avg, row.closepitch_stdev),
        },
        !isLive && {
            id: 'wastepitch%',
            title: translate('sections.reports.pitcher.waste-pitch-pct'),
            statistic: 'WastedPitPercent',
            transform: (row) => ratio((row.wastepitch || row.noncomppitches), row.pitches),
            type: colType.PERCENT,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass(row.wastepitch, row.pitches, row.wastepitch_avg, row.wastepitch_stdev, true),
        },
        // Missing Bats
        {
            id: 'ms%',
            title: translate('sections.reports.pitcher.abbr-miss-pct'),
            statistic: 'Ms',
            transform: (row) => ratio(row.ms, row.sw),
            type: colType.PERCENT,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass(row.ms, row.sw, row.ms_avg, row.ms_stdev),
        },
        {
            id: 'calledstk%',
            title: translate('sections.reports.pitcher.called-k-pct'),
            statistic: 'CalledStk',
            transform: (row) => ratio(row.tkstk, row.pitches),
            type: colType.PERCENT,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass(row.tkstk, row.pitches, row.calledstrike_avg, row.calledstrike_stdev),
        },
        {
            id: 'ch%',
            title: translate('sections.opponent.player.abbr-chase-pct'),
            statistic: 'Ch',
            transform: (row) => ratio(row.ch, row.outz),
            type: colType.PERCENT,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass(row.ch, row.outz, row.ch_avg, row.ch_stdev),
        },
        {
            id: 'putaway%',
            statistic: 'PutAwayPct',
            title: translate('sections.reports.pitcher.put-away-pct'),
            transform: (row) => ratio(row.so, row.twostrikepitches),
            type: colType.PERCENT,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass(row.so, row.twostrikepitches, row.putaway_avg, row.putaway_stdev),
        },
        // Limiting Damage
        {
            id: 'gb%',
            title: translate('baseball.abbr-groundball-pct'),
            statistic: 'GBPercent',
            transform: (row) => ratio(row.gb, row.gb + row.ld + row.fb + row.pop),
            type: colType.PERCENT,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass(row.gb, row.gb + row.ld + row.fb + row.pop, row.gb_avg, row.gb_stdev),
        },
        {
            id: 'exitvelo',
            title: translate('sections.reports.pitcher.exit-velo'),
            statistic: 'ExitVelo',
            transform: (row) => row.exitvelo || row.hitvelocity,
            precision: 1,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass((row.exitvelo || row.hitvelocity), 1, row.exitvelo_avg, row.exitvelo_stdev, true),
        },
        !isLive && {
            id: 'wellhit%',
            title: translate('sections.reports.pitcher.well-hit-pct'),
            statistic: 'WellHitPercent',
            transform: (row) => ratio((row.wellhit_exctm95 >= 0 ? row.wellhit_exctm95 : row.wellhit), row.pawhitfx),
            type: colType.PERCENT,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass((row.wellhit_exctm95 >= 0 ? row.wellhit_exctm95 : row.wellhit), row.pawhitfx, row.wellhit_avg, row.wellhit_stdev, true),
        },
        // Overall Performance
        !isLive && showGameFxEra && IsMlbOrAAA && {
            id: 'gamefxera',
            statistic: 'GameFXERA',
            title: translate('sections.reports.pitcher.gamefx-era'),
            transform: (row) => (row.gameid ? rvERA(row.rvgamefx) : null),
            precision: 2,
            headerClass: 'text-center',
            cellClass: (row) => (row.gameid ? getStlyingClass(rvERA(row.rvgamefx), 1, row.gamefxera_avg, row.gamefxera_stdev, true) : null),
        },
        !isLive && IsMlbOrAAA && {
            id: 'hitfxera',
            statistic: 'HitFXERA',
            title: translate('sections.reports.pitcher.hitfx-era'),
            transform: (row) => (row.gameid ? null : rvERA(row.rvexp)),
            precision: 2,
            headerClass: 'text-center',
            cellClass: (row) => (row.gameid ? null : getStlyingClass(rvERA(row.rvexp), 1, row.hitfxera_avg, row.hitfxera_stdev, true)),
        },
        !isLive && {
            id: 'stuffera',
            statistic: 'StuffERA',
            title: translate('sections.reports.pitcher.stuff-era'),
            transform: (row) => rvERA(row.rvstuff),
            precision: 2,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass(rvERA(row.rvstuff), 1, row.stuffera_avg, row.stuffera_stdev, true),
        },
        !isLive && IsMlbOrAAA && {
            id: 'pfxera',
            statistic: 'PitchFXERA',
            title: translate('sections.reports.pitcher.pfx-era'),
            transform: (row) => rvERA(row.rvpitchfx),
            precision: 2,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass(rvERA(row.rvpitchfx), 1, row.pitchfxera_avg, row.pitchfxera_stdev, true),
        },
        !isLive && {
            id: 'execera',
            statistic: 'ExecERA',
            title: translate('sections.reports.pitcher.exec-era'),
            transform: (row) => rvERA(row.rvexecera),
            precision: 2,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass(rvERA(row.rvexecera), 1, row.execera_avg, row.execera_stdev, true),
        },
        !isLive && IsMlbOrAAA && {
            id: 'breakera',
            statistic: 'BreakERA',
            title: translate('sections.reports.pitcher.abbr-break-era'),
            transform: (row) => rvERA(row.rvbreak),
            precision: 2,
            headerClass: 'text-center',
            cellClass: (row) => getStlyingClass(rvERA(row.rvbreak), 1, row.breakera_avg, row.breakera_stdev, true),
        },
    ].filter((x) => x);
    const titleColumn = { id: 'title', title: '', type: colType.TEXT };
    const vidPlaylistCol = { id: 'videoplaylist', title: null, type: colType.NODE };
    const levelAbbrCol = { id: 'levelabbr', title: translate('common.abbr-level'), type: colType.TEXT };
    const colsMainTable = [vidPlaylistCol, titleColumn, levelAbbrCol, ...columns];

    const gamedata = gamestats.map((s) => {
        const { mlbStDev, levelAvg } = getLevelAvgAndStDev(pitchtypestdev, s.levelid, s.yearid);
        const cols = [vidPlaylistCol, { ...titleColumn }, ...columns];

        const drilldownParent = (
            <DrilldownParent
                key={s.gameid}
                columns={(window.$IsDugoutApp || gamestats.length === 0) ? cols.filter((c) => c.id !== 'videoplaylist') : cols}
                playerid={playerid}
                gameid={s.gameid}
                pitchtype={s.pitchtype}
                fxsubtype={s.fxsubtype}
                isInBats={isInBats}
                pitchTypeSplits={pitchTypeSplits}
                isLive={isLive}
                livePlaylistFn={livePlaylistFn}
            />
        );

        const drilldown = (isLive || window.$IsDugoutApp || gamestats.length === 0) ? null : (
            <tr key={`${s.gameid}-drilldown`}>
                <td colSpan={100} className="p-0">
                    <PitchLog className="border-0" playerid={playerid} gameid={s.gameid} pitchtype={s.pitchtype} fxsubtype={s.fxsubtype} isInBats={isInBats} useSubType={useSubType} />
                </td>
            </tr>
        );

        const videoparams = { pitcherid: playerid, gameid: s.gameid, fxpitchtype: s.pitchtype, fxsubtype: s.fxsubtype };
        // eslint-disable-next-line no-nested-ternary
        const videoplaylist = isLive ? <VideoPlaylistButton playlist={livePlaylistFn(videoparams)} /> : (!window.$IsDugoutApp && isInBats) ? (<VideoPlayerButton {...videoparams} />) : '-';
        return { ...s, title: s.gamedate, pitchlabel: s.pitches, drilldown: [drilldownParent, drilldown], forceIsDrilldownTableRow: true, videoplaylist, ...mlbStDev, ...levelAvg };
    });

    const seasondata = seasonstats.map((s) => {
        const drilldown = (window.$IsDugoutApp || s.isCustom) ? null : <GameLog key={`gamelog-${s.yearid}-${playerid}`} playerid={playerid} yearid={s.yearid} levelid={s.levelid} pitchtype={s.pitchtype} fxsubtype={s.fxsubtype} isInBats={isInBats} useSubType={useSubType} />;
        const { mlbStDev, levelAvg } = getLevelAvgAndStDev(pitchtypestdev, s.levelid, s.yearid);
        return { ...s, title: s.isCustom ? `${dateTranslate(translate, moment(startDate))} - ${dateTranslate(translate, moment(endDate))}` : s.yearid, pitchlabel: s.pitches, drilldown, ...mlbStDev, ...levelAvg };
    });

    const lgaverages = lgastats.map((s) => ({ ...s, pitchlabel: '--', title: `${islhp ? translate('baseball.lhp') : translate('baseball.rhp')} MLB ${translate('common.abbr-average')} ${s.yearid}` }));
    const rows = [...gamedata, ...seasondata, ...lgaverages];
    const getOverallPerformanceColspan = () => {
        let colspan = 2;

        if (showGameFxEra && IsMlbOrAAA) {
            colspan += 4;
        }
        if (!showGameFxEra && IsMlbOrAAA) {
            colspan += 3;
        }

        return colspan;
    };
    const superheader = (
        <SuperHeaderRow>
            { /* eslint-disable-next-line no-nested-ternary */ }
            <SuperHeaderCell colSpan={window.$IsDugoutApp ? 3 : (gamestats.length === 0 ? 4 : 5)}>&nbsp;</SuperHeaderCell>
            <SuperHeaderCell colSpan={1}>{translate('sections.reports.pitcher.usage')}</SuperHeaderCell>
            <SuperHeaderCell colSpan={isLive ? 5 : 6}>{translate('sections.reports.pitcher.pitch-profile')}</SuperHeaderCell>
            <SuperHeaderCell colSpan={isLive ? 3 : 4}>{translate('sections.reports.pitcher.release-metrics')}</SuperHeaderCell>
            <SuperHeaderCell colSpan={isLive ? 1 : 4}>{translate('sections.reports.pitcher.controlling-the-zone')}</SuperHeaderCell>
            <SuperHeaderCell colSpan={4}>{translate('sections.reports.pitcher.missing-bats')}</SuperHeaderCell>
            <SuperHeaderCell colSpan={isLive ? 2 : 3}>{translate('sections.reports.pitcher.limiting-damage')}</SuperHeaderCell>
            {!isLive && <SuperHeaderCell colSpan={getOverallPerformanceColspan()}>{translate('sections.reports.pitcher.overall-performance')}</SuperHeaderCell>}
        </SuperHeaderRow>
    );

    return (
        <DataTable
            sortable={false}
            superheader={superheader}
            columns={(window.$IsDugoutApp || gamestats.length === 0) ? colsMainTable.filter((c) => c.id !== 'videoplaylist') : colsMainTable}
            rows={rows}
            statTooltip={StatTooltip}
            striped
        />
    );
};

PitchTypeStats.propTypes = {
    playerid: PropTypes.number.isRequired,
    gamestats: PropTypes.arrayOf(PropTypes.shape({
        gamedate: PropTypes.string,
    })).isRequired,
    seasonstats: PropTypes.arrayOf(PropTypes.shape({
        yearid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })).isRequired,
    lgastats: PropTypes.arrayOf(PropTypes.shape({
        yearid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })).isRequired,
    islhp: PropTypes.bool,
    pitchtypestdev: PropTypes.arrayOf(PropTypes.object),
    isInBats: PropTypes.bool,
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    pitchTypeSplits: PropTypes.arrayOf(PropTypes.shape({})),
    isLive: PropTypes.bool,
    livePlaylistFn: PropTypes.func,
    useSubType: PropTypes.bool,
    showGameFxEra: PropTypes.bool,
};

PitchTypeStats.defaultProps = {
    islhp: undefined,
    pitchtypestdev: [],
    isInBats: false,
    startDate: null,
    endDate: null,
    pitchTypeSplits: null,
    isLive: false,
    useSubType: false,
    showGameFxEra: false,
    livePlaylistFn: () => {},
};

export default PitchTypeStats;
